import {ChevronRight, MoreHorizontal} from 'lucide-react';
import * as React from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {useBreadcrumbContext} from 'src/Breadcrumbs/BreadcrumbContext';
import Popover from 'src/Layer/Popover';
import {cn} from 'src/lib/utils';
import {logoUrl} from 'src/logoOptions';
import type {TrailEntry} from 'src/Breadcrumbs/BreadcrumbContext';

/**
 * Trails longer than this collapse their middle entries into a "…" popover:
 * first crumb, …, and the last (MAX_VISIBLE_CRUMBS - 2) crumbs stay visible.
 */
const MAX_VISIBLE_CRUMBS = 4;

const Separator = () => (
  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
);

const CrumbLink: React.FC<{entry: TrailEntry}> = ({entry}) => (
  <Link
    to={entry.href}
    className="flex max-w-48 min-w-0 items-center gap-1 text-muted-foreground no-underline hover:text-foreground"
  >
    {entry.logo && (
      <img
        src={logoUrl(entry.logo)}
        alt=""
        className="size-3.5 shrink-0 rounded-sm"
      />
    )}
    <span className="truncate">{entry.label}</span>
  </Link>
);

const OverflowMenu: React.FC<{entries: TrailEntry[]}> = ({entries}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleSelect = (entry: TrailEntry) => {
    setIsOpen(false);
    navigate(entry.href);
  };

  const content = (
    <div
      className={cn(
        'shadow-popover bg-popover text-popover-foreground outline-ring/50',
        'flex flex-col overflow-y-auto rounded-md py-1 outline-1',
      )}
      role="menu"
    >
      {entries.map((entry, i) => (
        <button
          key={i}
          type="button"
          role="menuitem"
          className={cn(
            'hover:bg-accent/50 cursor-pointer px-3 py-1.5 text-left text-sm',
            'max-w-64 truncate',
          )}
          onClick={() => handleSelect(entry)}
        >
          {entry.label}
        </button>
      ))}
    </div>
  );

  return (
    <Popover
      open={isOpen}
      popoverContents={content}
      popoverAnchor="bottom-left"
      onOutsideClick={() => setIsOpen(false)}
    >
      {(anchorRef) => (
        <button
          type="button"
          ref={anchorRef}
          aria-label="Show hidden breadcrumbs"
          className={cn(
            'flex cursor-pointer items-center rounded p-0.5',
            'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
          )}
          onClick={() => setIsOpen((open) => !open)}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      )}
    </Popover>
  );
};

export const Breadcrumb: React.FC = () => {
  const {trail} = useBreadcrumbContext();

  if (trail.length === 0) {
    return null;
  }

  const shouldCollapse = trail.length > MAX_VISIBLE_CRUMBS;
  const head = shouldCollapse ? [trail[0]] : trail.slice(0, -1);
  const hidden = shouldCollapse
    ? trail.slice(1, trail.length - (MAX_VISIBLE_CRUMBS - 2))
    : [];
  const tail = shouldCollapse
    ? trail.slice(trail.length - (MAX_VISIBLE_CRUMBS - 2), -1)
    : [];
  const last = trail[trail.length - 1];

  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-lg">
      {head.map((entry, i) => (
        <React.Fragment key={`head-${i}`}>
          {i > 0 && <Separator />}
          <CrumbLink entry={entry} />
        </React.Fragment>
      ))}
      {hidden.length > 0 && (
        <>
          <Separator />
          <OverflowMenu entries={hidden} />
        </>
      )}
      {tail.map((entry, i) => (
        <React.Fragment key={`tail-${i}`}>
          <Separator />
          <CrumbLink entry={entry} />
        </React.Fragment>
      ))}
      {trail.length > 1 && <Separator />}
      {last.logo && (
        <img
          src={logoUrl(last.logo)}
          alt=""
          className="size-4 shrink-0 rounded-sm"
        />
      )}
      <span
        aria-current="page"
        className="min-w-0 truncate font-medium text-foreground"
      >
        {last.label}
      </span>
    </nav>
  );
};
